import { FacilityDetailsResponse } from "@features/ax/assetRegistry/facilities/types";
import { ProgressItem } from "@features/ax/shared/progressItem";
import { logEvent } from "@shared/analytics/analytics";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { PageHeader } from "@shared/components/PageHeader";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { Cpu, DollarSign, FlaskConical, Info } from "lucide-react";
import React, { useEffect } from "react";
import { useFacilityPerformance } from "../../../../hooks";
import { KpiSection } from "./kpiSection";
import { OperationalInsights } from "./operationalInsights";

interface FacilityPerformanceTabProps {
  facility: FacilityDetailsResponse;
}

const ComplianceParameter = ({
  title,
  value,
  unit,
  badge,
  target,
  tooltip,
}) => (
  <div className="p-4 border rounded-lg">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700">{title}</span>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-3 w-3 text-gray-400" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <Badge variant="default" className={badge.className}>
        {badge.text}
      </Badge>
    </div>
    <div className="flex items-baseline gap-1">
      <span className={`text-2xl font-bold ${badge.valueColor}`}>{value}</span>
      <span className="text-sm text-gray-500">{unit}</span>
    </div>
    <p className="text-xs text-gray-500 mt-1">{target}</p>
  </div>
);

export const FacilityPerformanceTab: React.FC<FacilityPerformanceTabProps> = ({
  facility,
}) => {
  const {
    data: performanceData,
    isLoading,
    error,
  } = useFacilityPerformance(facility.id);

  const currency = useCurrency();

  useEffect(() => {
    logEvent("performance_tab_viewed");
  }, []);

  const getBadgeStyle = (complianceRate: string) => {
    if (complianceRate === "Compliant") {
      return {
        text: complianceRate,
        className: "bg-green-100 text-green-800",
        valueColor: "text-green-600",
      };
    } else if (complianceRate === "Above Maximum") {
      return {
        text: complianceRate,
        className: "bg-yellow-100 text-yellow-800",
        valueColor: "text-yellow-600",
      };
    } else {
      return {
        text: complianceRate,
        className: "bg-red-100 text-red-800",
        valueColor: "text-red-600",
      };
    }
  };

  const parameterTooltips: { [key: string]: string } = {
    Turbidity:
      "Measures water cloudiness. Critical indicator of filtration effectiveness and potential pathogen presence.",
    "Chlorine Residual":
      "Active disinfectant remaining after treatment. Ensures pathogen elimination and distribution network safety.",
    "pH Level":
      "Water acidity/alkalinity. Affects disinfection efficiency and pipe network corrosion rates.",
    Temperature:
      "Water temperature affects biological activity and treatment chemical effectiveness.",
    "Total Coliforms":
      "Bacteria indicating potential fecal contamination. Must be absent in finished drinking water for public health.",
    "Water Hardness":
      "Dissolved minerals (calcium, magnesium). Affects pipe scaling and soap performance. Key aesthetic parameter.",
  };

  // Updated to use new API water quality compliance data
  const waterQualityData =
    performanceData?.result?.complianceWaterQuality || [];

  const apiParameters = waterQualityData?.map((param) => ({
    title: param.parameter || "N/A",
    value: param.avgTotalReadings || 0,
    unit: param.unit || "N/A",
    badge: getBadgeStyle(param.deviationType || "N/A"),
    target: `Range: ${param.avgMin} - ${param.avgMax} ${param.unit}`,
    tooltip:
      parameterTooltips[param.parameter] ||
      `${param.parameter} compliance monitoring for water quality standards.`,
  }));

  // Combine API and hardcoded parameters
  const complianceParameters = [...apiParameters];

  const healthDist = performanceData?.result?.assetHealthDistribution;

  const assetHealthDistribution = healthDist
    ? [
        {
          condition: "Excellent Condition",
          count: `${healthDist?.excellent?.count} (${healthDist?.excellent?.percentage}%)`,
          color: "bg-green-600",
        },
        {
          condition: "Good Condition",
          count: `${healthDist?.good?.count} (${healthDist?.good?.percentage}%)`,
          color: "bg-green-400",
        },
        {
          condition: "Fair Condition",
          count: `${healthDist?.fair?.count} (${healthDist?.fair?.percentage}%)`,
          color: "bg-yellow-400",
        },
        {
          condition: "Poor Condition",
          count: `${healthDist?.poor?.count} (${healthDist?.poor?.percentage}%)`,
          color: "bg-orange-500",
        },
        {
          condition: "Critical Condition",
          count: `${healthDist?.critical?.count} (${healthDist?.critical?.percentage}%)`,
          color: "bg-red-600",
        },
      ]
    : [];

  // Updated asset age profile to use new API structure (array instead of object)
  const assetAgeProfile =
    performanceData?.result?.assetAgeProfile?.map((item) => ({
      range: item.range,
      count: item.count,
      color: item.color, // Use color from API
    })) || [];

  // Updated financial metrics to use new API structure
  const formatFinancialValue = (value: number | string): string => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (numValue >= 1000000) {
      return `${currency} ${(numValue / 1000000).toFixed(1)}M`;
    } else if (numValue >= 1000) {
      return `${currency} ${(numValue / 1000).toFixed(0)}K`;
    } else {
      return `${currency} ${numValue?.toLocaleString()}`;
    }
  };

  const maintenanceData = performanceData?.result?.maintenanceAndWorkManagement;

  const financialMetrics = [
    {
      title: "Total Asset Value",
      value: formatFinancialValue(maintenanceData?.totalAssetCost || "0"),
      subtext: "Current book value",
    },
    {
      title: "Maintenance Spend",
      value: formatFinancialValue(maintenanceData?.totalCost || 0),
      subtext: "Current period",
    },
    {
      title: "Budget Variance",
      value:
        maintenanceData?.variancePercentage !== undefined
          ? `${maintenanceData.variancePercentage > 0 ? "+" : ""}${
              maintenanceData.variancePercentage
            }%`
          : "0%",
      subtext: "vs planned spending",
      valueColor:
        maintenanceData?.variancePercentage > 0
          ? "text-red-600"
          : "text-green-600",
    },
    {
      title: "Emergency Repairs",
      value: formatFinancialValue(maintenanceData?.unPlannedCost || 0),
      subtext: "Unplanned costs",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
          <Cpu className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <PageHeader
            title="Facility Performance Overview"
            subtitle="Comprehensive facility health, performance, and financial metrics"
          />
        </div>
      </div>

      <KpiSection facilityId={facility.id} />

      {/* <EnergyConsumptionSection facilityId={facility.id} /> */}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            <CardTitle className="text-lg">
              Compliance & Water Quality
            </CardTitle>
            <CustomTooltip message="Critical water quality parameters and compliance metrics ensuring public health and safety standards." />
          </div>
        </CardHeader>

        <CardContent>
          {complianceParameters && complianceParameters.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {complianceParameters.map((param) => (
                <ComplianceParameter key={param.title} {...param} />
              ))}
            </div>
          ) : (
            <div className="p-8 border rounded-lg">
              <p className="text-gray-500 text-center">No data found</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg flex items-center gap-2">
                Asset Health Status Distribution
              </CardTitle>
              <CustomTooltip
                message={
                  "Breakdown of assets by condition rating based on inspections, performance data, and maintenance history."
                }
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {assetHealthDistribution.length > 0 ? (
                assetHealthDistribution.map((item) => (
                  <div
                    key={item.condition}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 ${item.color} rounded`}></div>
                      <span className="text-sm">{item.condition}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold">{item.count}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Asset health distribution data not available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg flex items-center gap-2">
                Asset Age Profile
              </CardTitle>
              <CustomTooltip
                message={
                  "Distribution of assets by age ranges to help plan for upcoming replacements and maintenance needs."
                }
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <ProgressItem
                items={performanceData?.result?.assetAgeProfile}
                maxValue={100}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Financial Overview
            </CardTitle>
            <CustomTooltip
              message={
                "Comprehensive financial metrics including asset values,maintenance costs, budget performance, and lifecycle costs."
              }
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {financialMetrics.map((metric) => (
              <div key={metric.title} className="text-center">
                <p className="text-sm text-gray-600">{metric.title}</p>
                <p className={`text-xl font-bold ${metric.valueColor || ""}`}>
                  {metric.value}
                </p>
                <p className="text-xs text-gray-500">{metric.subtext}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <OperationalInsights performanceData={performanceData} />
    </div>
  );
};
